import { useCallback, useEffect } from 'react';
import { AfmPromoState, getAfmPromoState, saveAfmPromoState } from './afmPromoStorage';

// =============================================================================
// Policy
//
// Pure functions (no React, no localStorage) that own the promo's business
// rules: the submission threshold, the suppression window, draft dedupe, and the
// final eligibility decision. They take the persisted state in and return values
// or new state — so they are unit-tested directly, without rendering the hook or
// stubbing storage. The hook below is the only thing that reads/writes storage.
// =============================================================================

/**
 * Cumulative submitted-draft count required before the promo can be shown.
 * The requirement is "more than 3 submissions", i.e. the promo first becomes
 * eligible on the 4th cumulative submission.
 */
export const AFM_PROMO_SUBMITTED_THRESHOLD = 4;

/** Number of days the promo is suppressed after a dismissal (X / continue manually). */
export const AFM_PROMO_DISMISS_DAYS = 14;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * "Exhausted" = the user has at least one draft and every draft is in a terminal
 * state. Deleted drafts are removed server-side and never returned, so once the
 * user has dealt with everything only SUBMITTED drafts remain in the list. An
 * empty list is NOT exhausted: the promo only ever renders on the success screen
 * (which requires at least one submitted draft, so draftsMap is non-empty there).
 * Treating an empty list as exhausted would let shouldShowPromo() return true on
 * the error / nothing-submitted screens, which do not render the modal and would
 * strand the user on a navigation-prevented page.
 */
export function areAllDraftsExhausted(drafts: ReadonlyArray<{ state: string }>): boolean {
    return drafts.length > 0 && drafts.every((draft) => draft.state === 'SUBMITTED');
}

/**
 * Cumulative submitted count *as if* the given submitted draft ids were recorded,
 * without mutating state. Mirrors {@link recordSubmittedDrafts}' dedupe (against
 * already-counted ids and within the input) so eligibility can be evaluated
 * synchronously at click time instead of depending on the recording effect having
 * already run.
 */
export function projectedSubmittedCount(state: AfmPromoState, submittedDraftIds: string[]): number {
    const counted = new Set(state.countedDraftIds);
    const newIds = new Set(submittedDraftIds.filter((id) => id && !counted.has(id)));
    return state.submittedCount + newIds.size;
}

/**
 * Returns a new state with any not-yet-counted submitted draft ids folded into
 * the cumulative total. Deduplicates against the already-counted ids and within
 * the input. Returns the original state reference unchanged when nothing is new.
 */
export function recordSubmittedDrafts(
    state: AfmPromoState,
    submittedDraftIds: string[],
): AfmPromoState {
    const counted = new Set(state.countedDraftIds);
    const newIds = [...new Set(submittedDraftIds.filter((id) => id && !counted.has(id)))];
    if (newIds.length === 0) {
        return state;
    }
    return {
        ...state,
        countedDraftIds: [...state.countedDraftIds, ...newIds],
        submittedCount: state.submittedCount + newIds.length,
    };
}

/** True when the promo must not be shown (permanently opted in, or within the dismissal window). */
export function isSuppressed(state: AfmPromoState, now: number = Date.now()): boolean {
    return state.setUp || now < state.dismissedUntil;
}

/** Returns a new state suppressed for {@link AFM_PROMO_DISMISS_DAYS} days from `now`. */
export function withDismissal(state: AfmPromoState, now: number = Date.now()): AfmPromoState {
    return { ...state, dismissedUntil: now + AFM_PROMO_DISMISS_DAYS * MS_PER_DAY };
}

/** Returns a new state permanently opted out of the promo (entered Apply For Me setup). */
export function withPermanentOptIn(state: AfmPromoState): AfmPromoState {
    return { ...state, setUp: true };
}

/**
 * The promo's eligibility predicate, given all the facts it needs: whether the
 * current drafts are exhausted, the (projected) cumulative submitted count, and
 * whether the promo is currently suppressed.
 */
export function isPromoEligible(facts: {
    exhausted: boolean;
    submittedCount: number;
    suppressed: boolean;
}): boolean {
    if (!facts.exhausted) return false;
    if (facts.submittedCount < AFM_PROMO_SUBMITTED_THRESHOLD) return false;
    return !facts.suppressed;
}

// =============================================================================
// React adapter
// =============================================================================

/**
 * React adapter for the Apply For Me upsell promo.
 *
 * This is the *only* module that touches the promo's persisted state: it records
 * the cumulative submitted-draft count on load and exposes the eligibility check
 * and suppression writes the success page wires into its button handlers. All
 * decisions are delegated to the pure policy functions above.
 *
 * @param options - Hook inputs.
 * @param options.draftIds - Applyable draft ids for the current session (success page `?draftIds=`).
 * @param options.draftsMap - All drafts returned by the `findReviewBeforeSubmitDrafts` query,
 *   keyed by draft id. Only read at click time, by which point the success page has
 *   already gated rendering on the query resolving, so no explicit ready flag is needed.
 * @param options.enabled - Whether the promo experiment is on for this user
 *   (`rnhp_auto_apply_banner_tst` bucket 2). Inert when false.
 * @returns Success-page handlers:
 *   - `shouldShowPromo()` — true if the promo should show now (threshold met, drafts
 *     exhausted, not suppressed); call at click time.
 *   - `suppressPermanently()` — permanently suppress the promo (user entered Apply For Me setup).
 *   - `suppressForDismissal()` — suppress for the dismissal window (X / continue manually).
 */
export function useApplyForMePromo(options: {
    draftIds: string[];
    draftsMap: ReadonlyMap<string, { state: string }>;
    enabled: boolean;
}): {
    shouldShowPromo: () => boolean;
    suppressPermanently: () => void;
    suppressForDismissal: () => void;
} {
    const { draftIds, draftsMap, enabled } = options;

    // Persist the cumulative count of submitted drafts (deduped by draft id) so
    // the promo can be gated on a cross-session submission total. No-ops until the
    // drafts query resolves, since draftsMap is empty until then.
    useEffect(() => {
        if (!enabled) return;
        const submittedDraftIds = draftIds.filter((id) => draftsMap.get(id)?.state === 'SUBMITTED');
        if (submittedDraftIds.length === 0) return;
        const state = getAfmPromoState();
        const next = recordSubmittedDrafts(state, submittedDraftIds);
        if (next !== state) {
            saveAfmPromoState(next);
        }
    }, [enabled, draftIds, draftsMap]);

    const shouldShowPromo = useCallback(() => {
        if (!enabled) return false;
        const state = getAfmPromoState();
        // Exhaustion checks ALL drafts from the findReviewBeforeSubmitDrafts query
        // (draftsMap; deleted drafts are absent), not just this page's draftIds.
        const draftsList = [...draftsMap.values()];
        const exhausted = areAllDraftsExhausted(draftsList);
        // Fold the in-view submitted drafts into the persisted total so the very
        // first back-to-home click counts this session's submissions, even if the
        // recording effect above has not flushed yet.
        const submittedDraftIds = draftIds.filter((id) => draftsMap.get(id)?.state === 'SUBMITTED');
        return isPromoEligible({
            exhausted,
            submittedCount: projectedSubmittedCount(state, submittedDraftIds),
            suppressed: isSuppressed(state),
        });
    }, [enabled, draftIds, draftsMap]);

    const suppressPermanently = useCallback(() => {
        saveAfmPromoState(withPermanentOptIn(getAfmPromoState()));
    }, []);

    const suppressForDismissal = useCallback(() => {
        saveAfmPromoState(withDismissal(getAfmPromoState()));
    }, []);

    return { shouldShowPromo, suppressPermanently, suppressForDismissal };
}
